import {
    ChevronLeft,
    ClipboardList,
    FileText,
    Goal,
    Grid2X2,
    MapPin,
    NotebookPen,
} from "lucide-react";
import type { ReactNode } from "react";
import { toAbsoluteUrl } from "../../../../_theme/helpers";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import ThemeTooltip from "../../apps/components/UI/ThemeTooltip";
import { Overview } from "./tabs/overview";
import { Evaluation } from "./tabs/Evaluation";
import { useProjectDataQuery } from "./core/_queries";
import SimpleLoader from "../../apps/components/SimpleLoader";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getEvaluationPrinciples } from "./core/_request";
import { Principle } from "./core/_models";
import { useSnackbar } from "../../../../_theme/context/SnackbarContext";
import { LogError } from "../../../../utils/GlobalLogging";
import { DocumentTable } from "./tabs/documents";
import { AdditionalNotes } from "./tabs/AdditionalNotes";
import { useAuth, usePermission } from "../../auth";
import PermissionGate from "../../apps/components/UI/PermissionGate";
import { getStatusBadge, getStatusConfig, getStatusText } from "./core/_utils";
import { getApiErrorMessage } from "../../../../utils/utils";
import { isAbortedError } from "../../../../utils/global";

function renderStatusIcon(
    status?: string,
    size: number = 16,
    className: string = ""
): ReactNode {
    const config = getStatusConfig(status);
    const IconComponent = config.icon;

    return (
        <IconComponent
            size={size}
            className={className || `text-${config.color}`}
        />
    );
}

const PET = () => {
    const [activeTab, setActiveTab] = useState(1);
    const [evaluationPrinciples, setEvaluationPrinciples] = useState<Principle[]>([]);
    const [isLoadingPrinciples, setIsLoadingPrinciples] = useState(false);
    const [evalStatus, setEvalStatus] = useState("");
    const tab = useParams().tab;

    const { project_id } = useParams();
    const [searchParams] = useSearchParams();
    const returnTo = searchParams.get("return_to");
    const { auth } = useAuth()
    const { can } = usePermission()
    const { showSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const abortControllerRef = useRef<AbortController | null>(null);

    const projectId = Number(project_id);

    const {
        isLoading,
        projectDetails,
    } = useProjectDataQuery(projectId);

    const fetchEvaluationPrinciples = useCallback(async () => {
        if (!projectId || Number.isNaN(projectId)) {
            return;
        }

        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            setIsLoadingPrinciples(true);

            const response = await getEvaluationPrinciples(
                projectId,
                controller.signal
            );

            if (controller.signal.aborted) return;

            if (response.status === "ok") {
                setEvaluationPrinciples(
                    response.data.principles ?? []
                );
                return;
            }

            showSnackbar(
                response.message ||
                "Failed to fetch evaluation principles",
                "danger"
            );
        } catch (error) {
            if (isAbortedError(error)) return;

            LogError(
                "Error fetching evaluation principles:",
                error,
                "fetchEvaluationPrinciples"
            );

            showSnackbar(getApiErrorMessage(error, "Failed to fetch evaluation principles"),
                "danger"
            );
        } finally {
            if (!controller.signal.aborted) {
                setIsLoadingPrinciples(false);
            }
        }
    }, [projectId, showSnackbar]);

    useEffect(() => {
        fetchEvaluationPrinciples();
        return () => {
            abortControllerRef.current?.abort();
        };
    }, [fetchEvaluationPrinciples]);

    // Check if evaluation is completed
    const isEvaluationCompleted = useMemo(() => {
        return evalStatus === "Completed";
    }, [evalStatus]);

    // Build tabs based on evaluation status
    const tabs = useMemo(() => {
        // Base tabs that are always shown
        const baseTabs = [
            {
                id: 1,
                title: "Overview",
                icon: <NotebookPen size={16} />,
                content: (
                    <Overview
                        projectDescription={
                            projectDetails?.project_desc ?? "-"
                        }
                        evaluationPrinciples={evaluationPrinciples}
                        projectId={projectId}
                        setEvalStatus={setEvalStatus}
                    />
                ),
            },
            {
                id: 2,
                title: "Evaluation",
                icon: <ClipboardList size={16} />,
                content: (
                    <Evaluation
                        evaluationPrinciples={evaluationPrinciples}
                        projectId={projectId}
                    />
                ),
            },
            {
                id: 3,
                title: "Documents",
                icon: <FileText size={16} />,
                content: (
                    <DocumentTable
                        projectId={projectId}
                    />
                ),
            },
        ];

        // Only add Additional Notes tab if evaluation is completed
        if (isEvaluationCompleted) {
            return [
                ...baseTabs,
                {
                    id: 4,
                    title: "Additional Notes",
                    icon: <NotebookPen size={16} />,
                    content: (
                        <AdditionalNotes
                            projectId={projectId}
                        />
                    ),
                },
            ];
        }

        return baseTabs;
    }, [
        projectDetails?.project_desc,
        evaluationPrinciples,
        projectId,
        isEvaluationCompleted,
    ]);

    // Reset active tab when tabs change
    useEffect(() => {
        if (tabs.length > 0) {
            // Check if current active tab still exists
            const tabExists = tabs.some(tab => tab.id === activeTab);
            if (!tabExists) {
                // If not, set to first tab
                setActiveTab(tabs[0]?.id || 1);
            }
        }
    }, [tabs, activeTab]);

    // Set initial active tab when tabs load
    useEffect(() => {
        if (tab==="evaluation") {
            setActiveTab(2);
        }else if(tab==="documents"){
            setActiveTab(3);
        }else{
            setActiveTab(1);
        }
    }, [tabs, tab]);


    if (!projectId || Number.isNaN(projectId)) {
        return (
            <div className="container-fluid p-4">
                <div className="alert alert-danger">
                    Invalid project ID.
                </div>
            </div>
        );
    }

    if (isLoading || isLoadingPrinciples) {
        return <SimpleLoader />;
    }

    return (
        <div className="container-fluid p-4">
            <div className="bg-white rounded shadow-sm p-4">

                {/* Project Header */}
                <div className="card p-4 shadow-sm mb-4">
                    <div className="row">
                        <div className="col-md-6">
                            <div className="d-flex gap-3">
                                <div className="d-flex flex-column">
                                    <div className="d-flex align-items-center gap-2">
                                        <h2 className="fw-semibold mb-0">
                                            {projectDetails?.project_name || "-"}
                                        </h2>
                                        <ThemeTooltip
                                            text={projectDetails?.project_desc ?? "-"}
                                            theme="dark"
                                        />
                                    </div>

                                    <div className="d-flex gap-5">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="bg-danger bg-opacity-10 p-2 p-md-3 rounded-3">
                                                <MapPin
                                                    size={16}
                                                    className="text-danger"
                                                />
                                            </div>
                                            <div>
                                                <p className="text-gray-700 mb-0">District Municipality:</p>
                                                <p className="fw-bold text-gray-700 mb-0">
                                                    {projectDetails?.district_name ||
                                                        "N/A"}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-center gap-2">
                                            <div className="bg-success bg-opacity-10 p-2 p-md-3 rounded-3">
                                                <Goal
                                                    size={16}
                                                    className="text-success"
                                                />
                                            </div>
                                            <div>
                                                <p className="text-gray-700 mb-0">Local Municipality:</p>
                                                <span className="fw-bold text-gray-700">
                                                    {projectDetails?.municipality_name ||
                                                        "N/A"}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-center gap-2">
                                            <div className="bg-info bg-opacity-10 p-2 p-md-3 rounded-3">
                                                <Grid2X2
                                                    size={16}
                                                    className="text-info"
                                                />
                                            </div>
                                            <div>
                                                <p className="text-gray-700 mb-0">Sector:</p>
                                                <span className="fw-bold text-gray-700">
                                                    {projectDetails?.sector_name ||
                                                        "-"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="d-flex justify-content-end align-items-center gap-2">
                                {auth?.isAdmin !== 1 && <button
                                    className={`btn btn-sm ${getStatusBadge(
                                        getStatusText(evalStatus)
                                    )} text-white d-flex gap-1 align-items-center cursor-not-allowed`}
                                >
                                    {renderStatusIcon(evalStatus, 16, "text-white")}
                                    {getStatusText(evalStatus)}
                                </button>}

                                <PermissionGate allowed={can('panoptic_evaluation_summary_report', 'view')}>
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        className="d-flex align-items-center gap-1"
                                        onClick={() =>
                                            navigate(
                                                `/panoptic-evaluation-summary/${projectId}`
                                            )
                                        }
                                        disabled={projectDetails?.has_evaluation_summary_report_acccess === false}
                                    >
                                        <FileText size={16} />
                                        Project Evaluation Summary Report
                                    </Button>
                                </PermissionGate>

                                <Button
                                    variant="light"
                                    size="sm"
                                    className="d-flex align-items-center py-2 gap-1 h-40px"
                                    onClick={() =>
                                        navigate(
                                            returnTo ??
                                            "/dashboard/evaluation"
                                        )
                                    }
                                >
                                    <ChevronLeft size={16} />
                                    Back
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="shadow-sm rounded bg-light">
                    <div
                        className="d-flex align-items-start gap-2 min-width-max p-4 bg-white rounded"
                        role="tablist"
                    >
                        {tabs.map((tab) => {
                            const isActive =
                                activeTab === tab.id;

                            return (
                                <button
                                    key={tab.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={isActive}
                                    className={[
                                        "btn border-0 border-bottom border-bottom-2 px-3 py-0 d-flex align-items-center gap-2 fw-normal rounded-0",
                                        isActive
                                            ? "border-primary text-primary bg-transparent"
                                            : "border-transparent text-gray-700 bg-transparent",
                                    ].join(" ")}
                                    onClick={() =>
                                        setActiveTab(tab.id)
                                    }
                                >
                                    {tab.icon}

                                    <span className="fs-5 fw-semibold ms-2">
                                        {tab.title}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Active Tab Content */}
                <div
                    role="tabpanel"
                    className="p-4 bg-light rounded mt-4"
                >
                    {
                        tabs.find(
                            (tab) => tab.id === activeTab
                        )?.content
                    }
                </div>
            </div>
        </div >
    );
};

export { PET };